import { useTranslation } from 'react-i18next';
import type { Finding } from '@complan/contracts';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { formatDate } from '@/shared/format/date-format';
import { DateRangeField } from '@/shared/components/DateRangeField';
import type { SalesCycleFormValues, WriteErrorClassification } from './sales-cycle-model';

/**
 * The Sales_Cycle definition form (task 23.3, R6.1–R6.5).
 *
 * There is no dedicated Sales Cycle screen section in the UI spec, so the layout
 * is derived from the paired-card `grid2` pattern of §1.4 and the `.cond`/`.alert`
 * conventions of §6.1 (flagged in the task report): the field group sits in one
 * card, the active-state disclosure uses the `.cond` left-border treatment, and
 * the duplicate-name / inverted-range block and the overlap / gap warning both
 * render as `.alert` panels.
 *
 * The form is controlled by the parent page: it holds no persistence logic and no
 * data hook. It renders the current field values, reports edits upward, and — the
 * load-bearing part of R6.2/R6.3 — *never resets its own values*. A rejected save
 * flows in as a {@link WriteErrorClassification} and only adds error/warning
 * panels; the entered values stay exactly as the user left them.
 */
export interface SalesCycleFormProps {
  readonly values: SalesCycleFormValues;
  readonly onChange: (values: SalesCycleFormValues) => void;
  readonly onSubmit: () => void;
  readonly onReset: () => void;
  readonly isSubmitting: boolean;
  /** The classification of the last rejected save, or null when there is none. */
  readonly classification: WriteErrorClassification | null;
  /** Whether the pending warning acknowledgement is ticked (parent-owned). */
  readonly acknowledged: boolean;
  readonly onAcknowledgedChange: (acknowledged: boolean) => void;
  /** Whether an editing target is selected (changes the heading). */
  readonly isEditing: boolean;
}

export function SalesCycleForm({
  values,
  onChange,
  onSubmit,
  onReset,
  isSubmitting,
  classification,
  acknowledged,
  onAcknowledgedChange,
  isEditing,
}: SalesCycleFormProps): React.JSX.Element {
  const { t } = useTranslation();

  const blocked = classification?.kind === 'blocked' ? classification : null;
  const ackRequired =
    classification?.kind === 'acknowledgement_required' ? classification : null;
  const isFieldInvalid = (field: string): boolean =>
    blocked !== null && blocked.fields.includes(field);

  const handleField = <K extends keyof SalesCycleFormValues>(
    key: K,
    value: SalesCycleFormValues[K],
  ): void => {
    onChange({ ...values, [key]: value });
  };

  return (
    <Card className="gap-4 p-5">
      <div>
        <p className="text-[13px] font-semibold text-txt">
          {isEditing ? t('salesCycle.form.heading') : t('salesCycle.form.createHeading')}
        </p>
        <p className="text-muted-foreground mt-1 text-[12.3px]">
          {t('salesCycle.form.description')}
        </p>
      </div>

      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-[12px] font-medium text-txt">
            {t('salesCycle.form.planningYear')}
            <input
              type="number"
              className="rounded-md border px-2 py-1.5 text-[13px]"
              value={values.planningYear}
              min={2000}
              max={2100}
              onChange={(event) =>
                handleField('planningYear', Number.parseInt(event.target.value, 10) || 0)
              }
            />
          </label>

          <label className="flex flex-col gap-1 text-[12px] font-medium text-txt">
            {t('salesCycle.form.name')}
            <input
              type="text"
              className={cn(
                'rounded-md border px-2 py-1.5 text-[13px]',
                isFieldInvalid('name') && 'border-destructive',
              )}
              aria-invalid={isFieldInvalid('name')}
              value={values.name}
              maxLength={50}
              placeholder={t('salesCycle.form.namePlaceholder')}
              onChange={(event) => handleField('name', event.target.value)}
            />
          </label>
        </div>

        {/* Compact date-range field; opens the calendar picker as a popover. */}
        <div>
          <p className="mb-2 text-[12px] font-medium text-txt">
            {t('salesCycle.form.startDate')} / {t('salesCycle.form.endDate')}
          </p>
          <DateRangeField
            value={
              values.startDate && values.endDate
                ? { startDate: values.startDate, endDate: values.endDate }
                : null
            }
            onChange={(range) => {
              if (range) {
                // Update both dates together
                onChange({
                  ...values,
                  startDate: range.startDate,
                  endDate: range.endDate,
                });
              } else {
                // Clear both dates
                onChange({
                  ...values,
                  startDate: '',
                  endDate: '',
                });
              }
            }}
            label={t('salesCycle.form.dateRangeLabel')}
            minDate="2020-01-01"
            maxDate="2035-12-31"
          />
        </div>

        {/* §6.1 `.cond` treatment: 3px accent left border, tinted background. */}
        <div className="rounded-r-[4px] border-l-[3px] border-accent-line bg-[#FAFCFE] px-3 py-2.5">
          <label className="flex items-center gap-2 text-[12.5px] font-medium text-txt">
            <input
              type="checkbox"
              checked={values.isActive}
              onChange={(event) => handleField('isActive', event.target.checked)}
            />
            {t('salesCycle.form.isActive')}
          </label>
          <p className="text-muted-foreground mt-1 text-[11.5px]">
            {t('salesCycle.form.isActiveHint')}
          </p>
        </div>

        {blocked !== null && <BlockedAlert findings={blocked.findings} />}
        {ackRequired !== null && (
          <WarningAlert
            findings={ackRequired.findings}
            acknowledged={acknowledged}
            onAcknowledgedChange={onAcknowledgedChange}
          />
        )}

        <div className="flex items-center gap-2">
          <Button type="submit" size="sm" disabled={isSubmitting || (ackRequired !== null && !acknowledged)}>
            {isSubmitting
              ? t('salesCycle.form.submitting')
              : ackRequired !== null
                ? t('salesCycle.warning.saveAnyway')
                : t('salesCycle.form.submit')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onReset} disabled={isSubmitting}>
            {t('salesCycle.form.reset')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** The `.alert` block for the R6.2/R6.3 hard blocks; entered values stay put. */
function BlockedAlert({ findings }: { findings: readonly Finding[] }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Alert variant="destructive">
      <AlertTitle>{t('error.validationFailed.title')}</AlertTitle>
      <AlertDescription>
        <ul className="list-disc pl-4">
          {findings.map((finding) => (
            <li key={finding.ruleId}>{t(finding.messageKey, { ...(finding.detail ?? {}) })}</li>
          ))}
        </ul>
        <p className="mt-1 text-[11.5px]">{t('salesCycle.error.valuesRetained')}</p>
      </AlertDescription>
    </Alert>
  );
}

/** The `.alert` block for the R6.4/R6.5 warnings, with the acknowledgement gate. */
function WarningAlert({
  findings,
  acknowledged,
  onAcknowledgedChange,
}: {
  readonly findings: readonly Finding[];
  readonly acknowledged: boolean;
  readonly onAcknowledgedChange: (acknowledged: boolean) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Alert>
      <AlertTitle>{t('salesCycle.warning.heading')}</AlertTitle>
      <AlertDescription>
        <p>{t('salesCycle.warning.instruction')}</p>
        {findings.map((finding) => (
          <WarningFindingDetail key={finding.ruleId} finding={finding} />
        ))}
        <label className="mt-2 flex items-center gap-2 text-[12px] font-medium text-txt">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => onAcknowledgedChange(event.target.checked)}
          />
          {t('salesCycle.warning.acknowledge')}
        </label>
      </AlertDescription>
    </Alert>
  );
}

/**
 * Renders one warning finding's affected cycles or ranges (R6.4, R6.5). Dates in
 * the finding `detail` are ISO wire strings, so each is put through the shared
 * `formatDate` before it reaches the message (R18.10).
 */
function WarningFindingDetail({ finding }: { finding: Finding }): React.JSX.Element | null {
  const { t } = useTranslation();
  const detail = finding.detail ?? {};

  const overlaps = Array.isArray(detail['overlaps'])
    ? (detail['overlaps'] as ReadonlyArray<Record<string, unknown>>)
    : [];
  const gaps = Array.isArray(detail['gaps'])
    ? (detail['gaps'] as ReadonlyArray<Record<string, unknown>>)
    : [];

  if (overlaps.length > 0) {
    return (
      <div className="mt-1">
        <p className="text-[12px] font-semibold">{t('salesCycle.warning.overlapTitle')}</p>
        <ul className="list-disc pl-4 text-[11.5px]">
          {overlaps.map((overlap, index) => (
            <li key={`${String(overlap['firstId'])}-${String(overlap['secondId'])}-${index}`}>
              {t('salesCycle.warning.overlapItem', {
                firstName: String(overlap['firstName'] ?? ''),
                secondName: String(overlap['secondName'] ?? ''),
                overlapStart: formatDate(String(overlap['overlapStart'])),
                overlapEnd: formatDate(String(overlap['overlapEnd'])),
              })}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (gaps.length > 0) {
    return (
      <div className="mt-1">
        <p className="text-[12px] font-semibold">{t('salesCycle.warning.gapTitle')}</p>
        <ul className="list-disc pl-4 text-[11.5px]">
          {gaps.map((gap, index) => (
            <li key={`${String(gap['start'])}-${String(gap['end'])}-${index}`}>
              {t('salesCycle.warning.gapItem', {
                planningYear: Number(gap['planningYear'] ?? 0),
                start: formatDate(String(gap['start'])),
                end: formatDate(String(gap['end'])),
              })}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // A warning with an unexpected shape: fall back to its own message key.
  return <p className="mt-1 text-[11.5px]">{t(finding.messageKey, { ...detail })}</p>;
}
